import React, { useCallback, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { Dropdown, Input } from 'antd';
import {
  CheckOutlined,
  DownOutlined,
  PlusOutlined,
  SearchOutlined,
} from '@ant-design/icons';
import PencilOutlineIcon from '../../common/icons/files/PencilOutlineIcon';
import { useDispatch } from 'react-redux';

import { isFilterActive } from '../GanttSchedule/ganttScheduleHelpers';
import { FILTER_VIEW_FIELDS } from '../schedule.constants';
import { updateFieldScheduleFilters } from '../state/schedule.actions';

const ALL_SHIFTS_KEY = 'all';
const CUSTOM_KEY = 'custom';
const ALL_SHIFTS_LABEL = 'All shifts';

const filtersFromView = (view) => {
  const next = {
    filterView: view?.id ?? null,
    filterViewName: view?.filterViewName ?? null,
  };

  FILTER_VIEW_FIELDS.forEach(({ field, defaultValue }) => {
    next[field] = view?.[field] ?? defaultValue;
  });

  return next;
};

export default function ScheduleFilterDropdown({
  filters = {},
  filterViews = [],
  onEdit,
  onSearchFocus,
  onSearchBlur,
}) {
  const dispatch = useDispatch();
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');

  const activeView = useMemo(
    () => filterViews.find((view) => view.id === filters.filterView),
    [filterViews, filters.filterView],
  );

  const hasActiveFilters = useMemo(() => isFilterActive(filters), [filters]);

  const label = activeView?.filterViewName
    || filters.filterViewName
    || (hasActiveFilters ? 'Custom' : ALL_SHIFTS_LABEL);

  const selectedKey = activeView?.id
    || filters.filterView
    || (hasActiveFilters ? CUSTOM_KEY : ALL_SHIFTS_KEY);

  const query = search.trim().toLowerCase();
  const visibleViews = useMemo(() => {
    const views = [
      { id: ALL_SHIFTS_KEY, filterViewName: ALL_SHIFTS_LABEL },
      ...filterViews,
    ];
    if (!query) return views;
    return views.filter((view) => view.filterViewName?.toLowerCase().includes(query));
  }, [filterViews, query]);

  const close = useCallback(() => {
    setOpen(false);
    setSearch('');
  }, []);

  const onSelectAll = useCallback(() => {
    dispatch(updateFieldScheduleFilters(filtersFromView(null)));
    close();
  }, [close, dispatch]);

  const onSelectView = useCallback((view) => {
    dispatch(updateFieldScheduleFilters(filtersFromView(view)));
    close();
  }, [close, dispatch]);

  const onEditView = useCallback((event, viewId) => {
    event.stopPropagation();
    close();
    onEdit(viewId);
  }, [close, onEdit]);

  const onAddView = useCallback(() => {
    close();
    onEdit('custom');
  }, [close, onEdit]);

  const onOpenChange = useCallback((next) => {
    setOpen(next);
    if (!next) setSearch('');
  }, []);

  const menu = (
    <div className="schedule-filter-dropdown-panel" onClick={(event) => event.stopPropagation()}>
      <div className="schedule-filter-dropdown-search-wrap">
        <Input
          className="schedule-filter-dropdown-search"
          placeholder="Search views..."
          value={search}
          prefix={<SearchOutlined style={{ color: 'lab(90.7162 -0.552952 -1.78909)' }} />}
          onChange={(event) => setSearch(event.target.value)}
          onFocus={onSearchFocus}
          onBlur={onSearchBlur}
          onMouseDown={(event) => event.stopPropagation()}
          bordered={false}
        />
      </div>
      {
        visibleViews.length > 0 && (
          <div className="schedule-filter-dropdown-heading">Views</div>
        )
      }
      {visibleViews.length === 0 && (
        <div className="schedule-filter-dropdown-empty">No views found.</div>
      )}
      {visibleViews.map((view) => {
        const isAllShifts = view.id === ALL_SHIFTS_KEY;
        const isSelected = selectedKey === view.id;
        return (
          <div
            key={view.id}
            className="schedule-filter-dropdown-row"
            role="button"
            tabIndex={0}
            onClick={() => (isAllShifts ? onSelectAll() : onSelectView(view))}
            onKeyDown={(event) => {
              if (event.key === 'Enter' || event.key === ' ') {
                if (isAllShifts) onSelectAll();
                else onSelectView(view);
              }
            }}
          >
            <span className="schedule-filter-dropdown-check" aria-hidden={!isSelected}>
              <CheckOutlined style={{ visibility: isSelected ? 'visible' : 'hidden' }} />
            </span>
            <span className="schedule-filter-dropdown-row-label">{view.filterViewName}</span>
            {!isAllShifts && (
              <button
                type="button"
                className="schedule-filter-dropdown-edit"
                aria-label={`Edit ${view.filterViewName}`}
                onClick={(event) => onEditView(event, view.id)}
              >
                <PencilOutlineIcon />
              </button>
            )}
          </div>
        );
      })}
      <div className="schedule-filter-dropdown-footer-divider" />
      <button type="button" className="schedule-filter-dropdown-add" onClick={onAddView}>
        <PlusOutlined />
        Add view
      </button>
    </div>
  );

  return (
    <Dropdown
      overlay={menu}
      trigger={['click']}
      placement="bottomLeft"
      visible={open}
      onVisibleChange={onOpenChange}
      overlayClassName="schedule-filter-dropdown"
    >
      <button type="button" className="schedule-filter-dropdown-trigger">
        <svg
          className="schedule-filter-dropdown-icon"
          xmlns="http://www.w3.org/2000/svg"
          width="16"
          height="16"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
          aria-hidden="true"
        >
          <path d="M12.83 2.18a2 2 0 0 0-1.66 0L2.6 6.08a1 1 0 0 0 0 1.83l8.58 3.91a2 2 0 0 0 1.66 0l8.58-3.9a1 1 0 0 0 0-1.83z" />
          <path d="M2 12a1 1 0 0 0 .58.91l8.6 3.91a2 2 0 0 0 1.65 0l8.58-3.9A1 1 0 0 0 22 12" />
          <path d="M2 17a1 1 0 0 0 .58.91l8.6 3.91a2 2 0 0 0 1.65 0l8.58-3.9A1 1 0 0 0 22 17" />
        </svg>
        <span className="schedule-filter-dropdown-label">{label}</span>
        <DownOutlined />
      </button>
    </Dropdown>
  );
}

ScheduleFilterDropdown.propTypes = {
  filters: PropTypes.shape({
    filterView: PropTypes.string,
    filterViewName: PropTypes.string,
  }),
  filterViews: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.string,
    filterViewName: PropTypes.string,
  })),
  onEdit: PropTypes.func.isRequired,
  onSearchFocus: PropTypes.func,
  onSearchBlur: PropTypes.func,
};

ScheduleFilterDropdown.defaultProps = {
  filters: {},
  filterViews: [],
  onSearchFocus: undefined,
  onSearchBlur: undefined,
};
